import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { createHistory } from '../../services/history';
import { getImageModelConfig, generateImage, type ImageModelConfig } from '../../services/imageGen';

const AI_IMAGE_CREDITS_PER_USE = 5;

export default function AIImagePage() {
  const [activeMenu, setActiveMenu] = useState('AI生图');
  const [prompt, setPrompt] = useState('未来科技城市夜景,霓虹灯闪烁,飞行汽车穿梭在高楼大厦之间,赛博朋克风格');
  const [selectedRatio, setSelectedRatio] = useState('1:1');
  const [generatedImage, setGeneratedImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [imageModelConfig, setImageModelConfig] = useState<ImageModelConfig | null>(null);

  useEffect(() => {
    const load = async () => {
      const [u, config] = await Promise.all([getCurrentUser(), getImageModelConfig()]);
      setUser(u);
      setImageModelConfig(config ?? null);
    };
    load();
  }, []);

  const ratios = [
    { id: '1:1', label: '1:1' },
    { id: '2:3', label: '2:3' },
    { id: '3:2', label: '3:2' },
    { id: '3:4', label: '3:4' },
    { id: '4:3', label: '4:3' },
    { id: '4:5', label: '4:5' },
    { id: '5:4', label: '5:4' },
    { id: '9:16', label: '9:16' },
    { id: '16:9', label: '16:9' },
    { id: '21:9', label: '21:9' }
  ];

  const randomPrompts = [
    '一位优雅的女性站在樱花树下，粉色花瓣飘落，柔和的阳光透过树枝洒下，唯美浪漫的氛围',
    '未来科技城市夜景，霓虹灯闪烁，飞行汽车穿梭在高楼大厦之间，赛博朋克风格',
    '宁静的山间湖泊，清晨薄雾缭绕，远处雪山倒映在湖面上，自然风光摄影',
    '可爱的猫咪坐在窗台上，温暖的阳光照射进来，毛茸茸的质感，治愈系插画风格',
    '宇宙星空中的宇航员，漂浮在璀璨的星云之间，地球在远处发光，科幻艺术风格',
    '古风美人撑着油纸伞走在江南小巷，青石板路，烟雨朦胧，中国风水墨画风格',
    '热带海滩日落，棕榈树剪影，橙红色的天空倒映在海面上，度假风情摄影',
    '魔法森林中的精灵，发光的蝴蝶环绕，神秘的蓝色光芒，奇幻插画风格'
  ];

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages: string[] = [];
      const maxImages = 3 - uploadedImages.length;
      const filesToProcess = Math.min(files.length, maxImages);
      
      for (let i = 0; i < filesToProcess; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result as string);
          if (newImages.length === filesToProcess) {
            setUploadedImages([...uploadedImages, ...newImages]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };

  const handleRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * randomPrompts.length);
    setPrompt(randomPrompts[randomIndex]);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    if (!user?.id) {
      setError('请先登录');
      return;
    }
    if (!imageModelConfig?.model_name) {
      setError('请先在后台配置并启用图片生成模型（类型选「图像」，如阿里 qwen-image-plus / 火山 Seedream）');
      return;
    }
    // 阿里走后端代理，不需要前端持有 api_key；其它供应商仍依赖直连密钥
    if (imageModelConfig.provider !== 'alibaba' && !imageModelConfig.api_key) {
      setError('当前启用的图片模型缺少可用密钥。请启用阿里图像模型，或在后台重新配置火山引擎/钱多多密钥');
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const deduct = await deductCredits(user.id, AI_IMAGE_CREDITS_PER_USE, '高精度文生图', undefined);
      if (!deduct.success) {
        setError(deduct.error ?? '积分不足');
        return;
      }
      const imageUrl = await generateImage(imageModelConfig, prompt, selectedRatio);
      setGeneratedImage(imageUrl);
      await createHistory(user.id, {
        type: 'tool',
        category: 'ai-image',
        prompt: prompt.trim(),
        output_url: imageUrl,
        status: 'succeeded',
        credits_used: AI_IMAGE_CREDITS_PER_USE,
        settings: { ratio: selectedRatio },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : '生成失败，请重试');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `ai-image-${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex min-w-0">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        
        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto w-full min-w-0">
            <h1 className="text-3xl font-bold mb-2">AI生图</h1>
            <p className="text-gray-400 text-sm mb-4">高精度文生图，输入描述词即可生成高质量图片，支持参考图与多种比例。</p>
            {error && (
              <div className="mb-4 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0">
              {/* Left Panel - Input Controls */}
              <div className="space-y-6 min-w-0">
                {/* Main Prompt */}
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-add-line text-blue-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">参考图片（可选，最多3张）</h2>
                  </div>
                  
                  {uploadedImages.length > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        {uploadedImages.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className="w-full h-32">
                              <img 
                                src={image} 
                                alt={`Upload ${index + 1}`} 
                                className="w-full h-full object-cover rounded-lg"
                              />
                            </div>
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <i className="ri-close-line text-white text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                      {uploadedImages.length < 3 && (
                        <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div className="flex items-center justify-center gap-2">
                            <i className="ri-add-line text-blue-400 text-xl"></i>
                            <p className="text-gray-400 text-sm">继续上传（还可上传{3 - uploadedImages.length}张）</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center">
                          <i className="ri-upload-cloud-line text-blue-400 text-3xl"></i>
                        </div>
                        <p className="text-gray-400 text-sm">拖拽图片到此处，或点击上传</p>
                        <p className="text-gray-500 text-xs">支持JPG/PNG格式，单张图片不超过10MB</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Prompt Input */}
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-quill-pen-line text-purple-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                      <h2 className="text-lg font-semibold">描述词</h2>
                    </div>
                    <button 
                      onClick={handleRandomPrompt}
                      className="px-4 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-sm hover:border-blue-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-refresh-line"></i>
                      随机提示词
                    </button>
                  </div>
                  
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="描述你想要生成的图片，例如：一位优雅的女性站在樱花树下，粉色花瓣飘落，柔和的阳光透过树枝洒下..."
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none h-32"
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">{prompt.length}/500</span>
                  </div>
                </div>

                {/* Aspect Ratio */}
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-aspect-ratio-line text-teal-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">画面比例</h2>
                  </div>
                  
                  <div className="grid grid-cols-5 gap-3">
                    {ratios.map((ratio) => (
                      <button
                        key={ratio.id}
                        onClick={() => setSelectedRatio(ratio.id)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          selectedRatio === ratio.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 hover:bg-[#252525] border border-gray-700'
                        }`}
                      >
                        {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      生成中...
                    </>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-fill"></i>
                      生成图片
                      <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded-full">{AI_IMAGE_CREDITS_PER_USE} 积分</span>
                    </>
                  )}
                </button>
              </div>

              {/* Right Panel - Preview */}
              <div className="space-y-6 min-w-0">
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800 sticky top-8">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-line text-green-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">生成效果</h2>
                  </div>
                  
                  <div className="bg-[#0a0a0a] rounded-lg overflow-hidden border border-gray-800 aspect-square flex items-center justify-center">
                    {isGenerating ? (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-400 text-sm">AI正在生成中，请稍候...</p>
                      </div>
                    ) : generatedImage ? (
                      <div className="relative w-full h-full group">
                        <img 
                          src={generatedImage} 
                          alt="Generated" 
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                          <button onClick={handleDownload} className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="下载">
                            <i className="ri-download-line text-white text-xl"></i>
                          </button>
                          <a href={generatedImage} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="新窗口打开">
                            <i className="ri-share-line text-white text-xl"></i>
                          </a>
                          <button onClick={() => setGeneratedImage('')} className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="清除">
                            <i className="ri-delete-bin-line text-white text-xl"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 text-gray-500">
                        <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center">
                          <i className="ri-image-line text-5xl"></i>
                        </div>
                        <p className="text-sm">生成的图片将在此展示</p>
                      </div>
                    )}
                  </div>

                  {/* Tips */}
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-lightbulb-line text-yellow-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                      <h3 className="text-sm font-semibold">生成技巧</h3>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-400">
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>让参考图片的画面尽可能简洁，这样能够更好地生成理想的效果</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>描述词越详细，生成的图片越符合预期，可以包含风格、光线、氛围等细节</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>点击"随机提示词"按钮可以获取灵感，帮助你快速开始创作</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
